import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { api, money } from "@/lib/api";
import { Target, Building2, Wallet, Gauge, MapPin, Clock } from "lucide-react";

const Q = ({ icon: Icon, n, title, hint, children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay }}
    className="rounded-2xl border border-line bg-white/[0.03] p-6">
    <div className="flex items-center gap-2 text-brand">
      <Icon size={18} />
      <span className="label-eyebrow text-forest/70">Question {n}</span>
    </div>
    <h3 className="font-serif text-xl md:text-2xl text-forest mt-2 leading-snug">{title}</h3>
    {hint && <p className="text-sm text-mutedText mt-1">{hint}</p>}
    <div className="mt-4">{children}</div>
  </motion.div>
);

const Pills = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2" data-testid={testId}>
    {options.map((o) => (
      <button key={o} data-testid={`${testId}-${o.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase()}`}
        onClick={() => onChange(o)}
        className={`rounded-full border px-4 py-2 text-sm font-medium transition ${value === o ? "border-brand bg-brand text-white" : "border-line text-forest/75 hover:border-brand/50"}`}>
        {o}
      </button>
    ))}
  </div>
);

export default function InvestorDiscovery() {
  const { session, data, next, back, mergeData } = useJourney();
  const d0 = data.investor_discovery || {};
  const [a, setA] = useState({
    objective: d0.objective || "",
    experience: d0.experience || "",
    owned_count: d0.owned_count || "",
    funds: d0.funds ?? "",
    equity_available: d0.equity_available || false,
    contribution: d0.contribution || "",
    location_stance: d0.location_stance || "",
    preferred_location: d0.preferred_location || "",
    horizon: d0.horizon || "",
    future_strategy: d0.future_strategy ?? null,
  });
  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));

  const buildProfile = async () => {
    if (!a.objective || !a.experience || !a.contribution || !a.location_stance || !a.horizon) {
      toast.error("Answer questions 1, 2, 4, 5 and 6 to build the profile.");
      return;
    }
    if (!a.funds || Number(a.funds) <= 0) {
      toast.error("Add the funds available in question 3.");
      return;
    }
    const investor_discovery = { ...a, funds: Number(a.funds) };
    mergeData({ investor_discovery });
    if (session) api.patchSession(session.id, { data: { investor_discovery } }).catch(() => {});
    next();
  };

  return (
    <StageFrame testId="stage-investor-discovery" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-3xl mx-auto">
        <Eyebrow>Investor Discovery</Eyebrow>
        <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-forest mt-3">
          Tell us what you want this property to achieve.
        </h1>
        <p className="text-mutedText mt-3">Six quick questions — then we find the three properties that best fit your goal.</p>

        <div className="mt-8 space-y-4">
          <Q icon={Target} n={1} title="What are you hoping this property will achieve for you?" delay={0.02}>
            <Pills testId="q-objective" value={a.objective} onChange={(v) => set("objective", v)}
              options={["Capital growth", "Strong rental income", "Balance of both", "First step into investing"]} />
          </Q>

          <Q icon={Building2} n={2} title="Is this your first investment property, or do you already own property?" delay={0.06}>
            <Pills testId="q-experience" value={a.experience} onChange={(v) => set("experience", v)}
              options={["This is my first", "I already own property"]} />
            {a.experience === "I already own property" && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm text-mutedText">How many do you own?</span>
                <input data-testid="q-owned-count" type="number" min="1" value={a.owned_count}
                  onChange={(e) => set("owned_count", e.target.value)} placeholder="1"
                  className="w-24 rounded-xl border border-line bg-white/5 px-3 py-2 text-forest focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>
            )}
          </Q>

          <Q icon={Wallet} n={3} title="How much do you have available to contribute toward the purchase?" delay={0.1}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mutedText">$</span>
                <input data-testid="q-funds" type="number" min="0" step="1000" value={a.funds}
                  onChange={(e) => set("funds", e.target.value)} placeholder="80,000"
                  className="w-44 rounded-xl border border-line bg-white/5 pl-7 pr-3 py-2.5 text-forest focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>
              <label className="flex items-center gap-2 text-sm text-forest/80 cursor-pointer">
                <input data-testid="q-equity" type="checkbox" checked={a.equity_available}
                  onChange={(e) => set("equity_available", e.target.checked)} className="accent-brand w-4 h-4" />
                Equity from another property may be available
              </label>
            </div>
          </Q>

          <Q icon={Gauge} n={4} title="What weekly contribution would you be comfortable making if the rent doesn't cover everything?" delay={0.14}>
            <Pills testId="q-contribution" value={a.contribution} onChange={(v) => set("contribution", v)}
              options={["$0–$50", "$50–$100", "$100–$200", "Flexible"]} />
          </Q>

          <Q icon={MapPin} n={5} title="A particular location — or shall we show you where the numbers look strongest?" delay={0.18}>
            <Pills testId="q-location" value={a.location_stance} onChange={(v) => set("location_stance", v)}
              options={["I have a preferred location", "Show me the opportunities"]} />
            {a.location_stance === "I have a preferred location" && (
              <input data-testid="q-preferred-location" value={a.preferred_location}
                onChange={(e) => set("preferred_location", e.target.value)} placeholder="e.g. Baldivis, or a corridor you like"
                className="mt-3 w-full rounded-xl border border-line bg-white/5 px-4 py-2.5 text-forest focus:outline-none focus:ring-2 focus:ring-brand" />
            )}
          </Q>

          <Q icon={Clock} n={6} title="How long do you see yourself holding this property?" delay={0.22}>
            <Pills testId="q-horizon" value={a.horizon} onChange={(v) => set("horizon", v)}
              options={["5 years", "10 years", "Long term", "Not sure yet"]} />
            <div className="mt-4 rounded-xl border border-line bg-white/5 p-4">
              <p className="text-sm text-forest/90">Would you like this property to potentially help you acquire another one as it grows?</p>
              <div className="mt-3 flex gap-2" data-testid="q-future-strategy">
                {[["Yes", true], ["Not right now", false]].map(([label, val]) => (
                  <button key={label} data-testid={`q-future-${String(val)}`} onClick={() => set("future_strategy", val)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition ${a.future_strategy === val ? "border-brand bg-brand text-white" : "border-line text-forest/75 hover:border-brand/50"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </Q>
        </div>
      </div>

      <ConsultantNav testId="stage-investor-discovery" onBack={back} onNext={buildProfile} nextLabel="Build my investor profile" />
    </StageFrame>
  );
}
